import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  audits,
  readSnapshot,
  type AuditAnswer,
  type AuditDetail,
  type SnapshotForm,
  type SnapshotQuestion,
} from '../api/audits';
import { completeness, isComplete, isSkipped, owed, progress } from '../audit/completeness';
import { copyOf, galleryAccept, judge, technical } from '../offline/evidence';
import { fold } from '../offline/fold';
import { forget, steady, type LinkMemory } from '../offline/links';
import { cache, outbox, type PendingOp } from '../offline/store';
import { concerning, keepSyncing, sync, type SyncOutcome } from '../offline/sync';
import { score as scoreLocally } from '../scoring/engine';
import { categoryPoints, categoryWorth, isRuledOut, questionWorth, runningPoints } from '../scoring/points';
import { RaiseAction } from './RaiseAction';
import { SignaturePad } from './Signature';
import {
  IconCamera,
  IconCheck,
  IconChevronDown,
  IconCircleAlert,
  IconClipboardList,
  IconCircleSlash,
  IconInfo,
  IconMessageSquare,
  IconUpload,
  IconVideo,
  IconListFilter,
  IconSend,
  IconSquare,
  IconTrash2,
  IconTriangleAlert,
} from '../icons';
import { formatDate, formatNumber } from '../i18n';
import { describeError, useTranslate } from '../i18n/react';
import { Badge } from '../ui/Badge';
import { Button, IconButton } from '../ui/Button';
import { CameraCapture, canOpenCamera } from '../ui/CameraCapture';
import { MediaViewer } from '../ui/MediaViewer';
import { RowMenu } from '../ui/RowMenu';
import { Drawer } from '../ui/Drawer';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { EmptyState } from '../ui/EmptyState';
import { Alert, Field, Textarea } from '../ui/Field';
import { PageHeader } from '../ui/Page';
import { SyncChip } from '../ui/SyncChip';
import { Thumbnail } from '../ui/Thumbnail';

/**
 * Filling in an audit, on a phone, in a stockroom.
 *
 * <p>The customer's one stated quality bar is speed: seventy questions in minutes, and
 * anything that adds a tap per question adds seventy taps. So the answer controls are the
 * first thing on each card and large enough to hit without looking, the categories start
 * shut so the list is navigable, and nothing asks for confirmation except the submission.</p>
 *
 * <p><b>Progress is a colour and a number.</b> Colour alone is not a progress indicator —
 * it says nothing to somebody who cannot distinguish the two greens, and nothing at all in
 * bright sun through a shop window.</p>
 *
 * <p><b>It works with no signal.</b> Nothing here calls the server. Every answer goes into
 * an outbox and the screen renders the server's last word with the outbox folded in, scored
 * by the engine that also runs on the server — so the running total and the list of what is
 * still owed are the same in a stockroom as they are at a desk.</p>
 *
 * <p>The replay happens in the background, in order, and stops at the first thing it cannot
 * deliver. What the auditor sees is a count of what is still waiting, which is the honest
 * version of a tick that means nothing.</p>
 */

/**
 * Wide on purpose, and it was narrow for a week.
 *
 * <p>The server takes a shorter list than a phone offers, so naming the exact types here
 * looked like the honest thing: the gallery would not offer what would be refused. It
 * does not work that way. Android's picker, asked for types an item cannot be provided
 * in, does not hide the item — it hands back a <b>converted</b> one. An auditor chose a
 * short video from the gallery and the picker delivered a forty-kilobyte still image
 * instead. It uploaded, it was stored, it was shown, and it was not the video: nothing
 * anywhere said a substitution had happened, and the only symptom was "the video does
 * not appear".</p>
 *
 * <p>Wide, the picker hands over what was actually chosen and the <b>server</b> refuses
 * what it cannot use, in words, naming the type — which for HEIC also says which iPhone
 * setting to change. A refusal somebody can read beats a conversion nobody is told
 * about. The accept attribute is a convenience filter; it was never the gate, and
 * treating it as one cost a silent substitution.</p>
 */
const PHOTOS = 'image/*';

/** The same, and for the same reason. The gate is AuditEvidence, on the server. */
const VIDEOS = 'video/*';

/**
 * One screen per audit, built afresh for each.
 *
 * <p><b>It used to be one screen that changed its audit, and it carried the last one
 * across.</b> React Router keeps an element mounted when only the parameter changes, so
 * moving from one audit to another kept everything held in state: the detail, the queue,
 * the refusals. Until the new audit loaded — and with no signal and nothing cached, it never
 * did — the screen showed the previous visit under the new address, and a file added there
 * was queued for the audit in the address beside a question from the other. Measured
 * locally: opened A, moved to an uncached C with the server unreachable, and the page read
 * "A-1" with A's refusals under C's address.</p>
 *
 * <p>Keyed by the identifier, so nothing about one visit can survive into another.</p>
 */
export function Audit() {
  const { id = '' } = useParams();
  return <AuditScreen key={id} id={id} />;
}

function AuditScreen({ id }: { id: string }) {
  const t = useTranslate();
  const navigate = useNavigate();

  const [stored, setStored] = useState<AuditDetail | null>(null);
  const [pending, setPending] = useState<Awaited<ReturnType<typeof outbox.forAudit>>>([]);
  const [form, setForm] = useState<SnapshotForm | null>(null);
  /**
   * The categories the auditor has opened.
   *
   * <p>Everything starts shut. Seventy questions in nine categories is a page nobody can
   * find their place on, and the auditor works a category at a time — the one they are
   * standing in front of. Opening is one tap; scrolling past eight categories to reach the
   * ninth is not.</p>
   */
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [rejected, setRejected] = useState<string[]>([]);

  // Queued, tried, and given up on for now — not refused, and not gone. Separate from
  // rejected because the answer is different: a refusal is final and this one may still
  // go, but not while the auditor keeps waiting for it in silence.
  const [stuck, setStuck] = useState(0);

  /**
   * Files that did not make it onto a question, by question, in words.
   *
   * <p>Beside the question rather than only at the top of the page. A file is added while
   * looking at question forty; a refusal printed above question one was a refusal nobody
   * saw — the picker closed, the tile never appeared or quietly went, and the auditor's
   * account was "nothing happens, no warning".</p>
   */
  const [refusedFiles, setRefusedFiles] = useState<Record<string, string[]>>({});
  // Once per reason: a replay already under way hands its outcome to every caller that
  // asked for one, so the same refusal can arrive twice.
  const refuseAt = (questionId: string, reason: string) =>
    setRefusedFiles((was) =>
      was[questionId]?.includes(reason)
        ? was
        : { ...was, [questionId]: [...(was[questionId] ?? []), reason] },
    );

  const [onlyMissing, setOnlyMissing] = useState(false);
  const [raising, setRaising] = useState<{ question: SnapshotQuestion; comment: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  // One address per queued file for as long as it is queued. See fold's objectUrl.
  const objectUrls = useRef(new Map<number, string>());
  const objectUrl = useCallback((pendingId: number, blob: Blob) => {
    let url = objectUrls.current.get(pendingId);
    if (!url) {
      url = URL.createObjectURL(blob);
      objectUrls.current.set(pendingId, url);
    }
    return url;
  }, []);

  // The address each file is being shown under, while it works. See steady.
  const links = useRef<LinkMemory>(new Map());

  /**
   * The cache first, then the network. An auditor who opens an audit in a basement sees it
   * immediately; one with a signal sees the same thing a moment later, fresher.
   */
  const load = useCallback(async () => {
    try {
      const local = await cache.get(id);
      if (local) {
        setStored(local);
        setForm(readSnapshot(local.formSnapshot));
        setLoading(false);
      }

      setPending(await outbox.forAudit(id));

      try {
        const fresh = await audits.get(id);
        await cache.put(fresh);
        setStored(fresh);
        setForm(readSnapshot(fresh.formSnapshot));
        setError(null);
      } catch (e) {
        // Only a problem if there was nothing cached. Otherwise this is simply a visit with
        // no signal, which is the normal case this screen exists for.
        if (!local) setError(describeError(e, 'error.listFailed'));
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * What a replay came back with, said at the top and, for a file of this audit, at its
   * question.
   *
   * <p>The replay walks every audit's queue, so its outcome is not this audit's. A refusal
   * for another visit was put under the question with the same identifier here — and two
   * audits on one form share every question identifier — so a file refused on one visit was
   * reported on the next one opened. See {@link concerning}.</p>
   */
  const heard = async (outcome: SyncOutcome) => {
    const { rejected: ours, elsewhere, stuck: ourStuck } = concerning(outcome, id);
    const reason = (r: (typeof ours)[number]) => {
      const why = describeError(r.error, 'error.actionFailed');
      return r.pending.op.kind === 'photo' ? `${r.pending.op.name}: ${why}` : why;
    };

    const lines = ours.map((r) => describeError(r.error, 'error.actionFailed'));

    for (const r of ours) {
      if (r.pending.op.kind === 'photo') refuseAt(r.pending.op.questionId, reason(r));
    }

    // Still said, because it was taken out of the queue and is said nowhere else — but
    // named, and never under one of this audit's questions.
    for (const r of elsewhere) {
      const other = await cache.get(r.pending.auditId).catch(() => null);
      const audit = other
        ? [other.summary.locationName, other.summary.reference].filter(Boolean).join(' · ')
        : r.pending.auditId.slice(0, 8);
      lines.push(t('audit.rejectedElsewhere', { audit, reason: reason(r) }));
    }

    if (lines.length > 0) setRejected(lines);
    setStuck(ourStuck);
  };

  // Replays in the background and refreshes what is on screen when something lands.
  useEffect(() => {
    const report = (outcome: SyncOutcome) => {
      void heard(outcome);
      if (outcome.sent > 0 || outcome.rejected.length > 0) void refresh();
    };

    return keepSyncing(report);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const refresh = useCallback(async () => {
    const local = await cache.get(id);
    if (local) setStored(local);
    setPending(await outbox.forAudit(id));
  }, [id]);

  /**
   * A working address for a file whose address did not load, from the server.
   *
   * <p>From the server, not from {@link refresh}: the viewer's "try again" used to call that,
   * which reads the device's cache — the same expired link, so the button did nothing.
   * Measured: pressing it made no request and the viewer stayed broken. The file's
   * remembered address is dropped first, or steady would hand the dead one back.</p>
   */
  const relink = useCallback(
    async (photoId: string) => {
      forget(links.current, photoId);

      const fresh = await audits.get(id);
      await cache.put(fresh);
      setStored(fresh);

      const url = fresh.answers.flatMap((a) => a.photos).find((p) => p.id === photoId)?.url;
      if (!url) throw new Error('The server gave no address for this file.');
      return url;
    },
    [id],
  );

  /**
   * Into the outbox and straight back to the screen. Nothing waits for the network, which
   * is the whole point: an answer tapped in a stockroom appears instantly and is sent when
   * there is something to send it over.
   */
  async function enqueue(op: PendingOp, onFail?: (error: unknown) => void) {
    try {
      await outbox.add(id, op);
      setPending(await outbox.forAudit(id));
      setError(null);
      void sync().then((outcome) => {
        void heard(outcome);
        if (outcome.sent > 0 || outcome.rejected.length > 0) void refresh();
      });
    } catch (e) {
      if (onFail) onFail(e);
      else setError(describeError(e, 'error.saveFailed'));
    }
  }

  /**
   * A chosen file, judged, read and only then queued.
   *
   * <p>What the server would refuse is refused here, at once and beside the question,
   * rather than after the whole file has gone over mobile data. A file whose type the phone
   * left empty is sent under the type its name gives. The bytes are read now, while the
   * picker's grant is fresh (see copyOf). A file that cannot be read, or kept, is said the
   * same way and with the browser's own name for what went wrong — a phone showed the
   * failure with only "could not save" beside it, which said that it happened and nothing
   * about why.</p>
   */
  async function addEvidence(questionId: string, file: File) {
    const verdict = judge(file);

    if (!verdict.ok) {
      refuseAt(questionId, `${file.name}: ${describeError({ code: verdict.code, args: verdict.args }, 'error.saveFailed')}`);
      return;
    }

    // A fresh attempt clears what was said about the last one.
    setRefusedFiles(({ [questionId]: _, ...rest }) => rest);

    let blob: Blob;

    try {
      blob = await copyOf(file, verdict.type);
    } catch (e) {
      refuseAt(questionId, `${file.name}: ${t('audit.fileNotRead')} ${technical(e)}`);
      return;
    }

    await enqueue(
      { kind: 'photo', questionId, blob, name: file.name, type: verdict.type },
      (e) => refuseAt(questionId, `${file.name}: ${t('audit.fileNotStored')} ${technical(e)}`),
    );
  }

  /** The server's last word with the outbox folded in. Everything below reads this. */
  const steadied = useMemo(
    () => (stored ? steady(stored, links.current, Date.now()) : null),
    [stored],
  );
  const folded = useMemo(
    () => (steadied ? fold(steadied, pending, objectUrl) : null),
    [steadied, pending, objectUrl],
  );
  const detail = folded?.detail ?? null;

  const answers = useMemo(() => {
    const map = new Map<string, AuditAnswer>();
    for (const a of detail?.answers ?? []) map.set(a.questionId, a);
    return map;
  }, [detail]);

  /**
   * Which questions are on screen. A question whose condition was not met was never shown,
   * so it is not missing — it does not exist for this visit.
   */
  const visible = useMemo(() => {
    const shown = new Map<string, boolean>();
    if (!form) return shown;

    for (const category of form.categories) {
      for (const question of category.questions) {
        if (!question.condition) {
          shown.set(question.id, true);
          continue;
        }

        const source = answers.get(question.condition.questionId);
        shown.set(
          question.id,
          (shown.get(question.condition.questionId) ?? true) &&
            source?.yesNo != null &&
            source.yesNo === question.condition.answer,
        );
      }
    }

    return shown;
  }, [form, answers]);

  /**
   * Scored here, by the engine that also runs on the server and is tested against the same
   * vectors. The server's own answer is in the cached detail and is the one of record; this
   * is what makes it visible before there is a signal to ask for it.
   */
  const running = useMemo(() => {
    if (!form || !detail) return null;

    return scoreLocally(
      {
        scored: form.scored,
        categories: form.categories.map((c) => ({
          id: c.id,
          name: c.name,
          weight: c.weight,
          questions: c.questions.map((q) => ({
            id: q.id,
            weight: q.weight,
            type: q.type,
            critical: q.critical,
            notApplicable: q.notApplicable,
            scale: q.scale,
            options: q.options,
            condition: q.condition,
          })),
        })),
      },
      detail.answers.map((a) => ({
        questionId: a.questionId,
        yesNo: a.yesNo,
        scaleValue: a.scaleValue,
        selectedOptionIds: a.selectedOptionIds,
        text: a.text,
        markedNotApplicable: a.markedNotApplicable,
        structurallyNotApplicable: a.structurallyNotApplicable,
      })),
    );
  }, [form, detail]);

  // Object URLs are never collected on their own, so each one is released as soon as the
  // file it stood for has left the queue — and not before, which is what objectUrl is for.
  useEffect(() => {
    const queued = new Set(pending.map((p) => p.id));

    for (const [pendingId, url] of objectUrls.current) {
      if (queued.has(pendingId)) continue;
      URL.revokeObjectURL(url);
      objectUrls.current.delete(pendingId);
    }
  }, [pending]);

  // And all of them when the screen goes.
  useEffect(() => {
    const urls = objectUrls.current;
    return () => {
      for (const url of urls.values()) URL.revokeObjectURL(url);
      urls.clear();
    };
  }, []);

  if (loading) return <p className="nv-hint">{t('common.loading')}</p>;
  if (!folded || !detail || !form || !running) {
    return (
      <EmptyState
        icon={IconTriangleAlert}
        tone="danger"
        title={error ?? t('audit.notFound')}
        action={<Button onClick={() => navigate('/denetimler')}>{t('audit.backToList')}</Button>}
      />
    );
  }

  const locked = detail.summary.status !== 'Draft';

  const counts = (questions: SnapshotQuestion[]) => {
    // Hidden by a condition means not owed: counting it would make a visit impossible
    // to finish. Everything else about "finished" lives in one module, because the header
    // and the question card used to decide it separately and disagree.
    return progress(questions.filter((q) => visible.get(q.id) !== false), answers);
  };

  // The signature is part of finishing the visit, so it is part of the count — but not
  // of any category's, because it belongs to none of them.
  /*
   * What the customer has chosen to show. Read off the audit rather than off a settings
   * call, so it is there when the device is not on a network — and read fresh on every
   * fetch, so turning one on reaches a visit already in progress.
   *
   * Defaulted to showing: a cached audit fetched before these existed has neither field,
   * and the honest reading of a document that does not say is what it was doing.
   */
  const showPoints = detail?.showQuestionPoints ?? true;
  const showScore = (detail?.showRunningScore ?? true) && (form?.scored ?? false);

  /** This category's line in the running result, or undefined before the first score. */
  const scoreOf = (categoryId: string) =>
    running?.categories.find((c) => c.categoryId === categoryId);

  /**
   * Under the points model the figures read as points earned, not as a ratio: 25 for a
   * twenty-five point category answered in full, where the ratio says 100. See points.ts.
   */
  const byPoints = form?.scoring === 'Points';
  const categoryFigure = (categoryId: string) => {
    const line = scoreOf(categoryId);
    if (!line || !running) return null;
    return byPoints ? categoryPoints(running, line) : line.score;
  };
  const runningFigure = running ? (byPoints ? runningPoints(running) : running.runningScore) : null;

  /**
   * What a category is worth on this visit, written as the change when there is one.
   *
   * <p>A category whose every question was ruled out is not applicable, and the others carry
   * its share (ADR-022): fifty declared can be worth 62,5 today. Printing the 62,5 alone would
   * leave the designer who typed fifty with no idea where it came from; printing both says
   * what happened without a sentence. A question ruled out inside the category changes
   * nothing here — the category keeps its worth and its other questions carry it.</p>
   */
  const weightLabel = (categoryId: string) => {
    const line = scoreOf(categoryId);
    if (!line || !running) return null;

    const declared = round(line.declaredWeight);

    // What it is worth today, not what the running sum is currently using. Those differ
    // until something in the category has been scored, and the sum's figure beside a
    // category name read as "this is now worth nothing" on a visit nobody had started.
    const worth = categoryWorth(running, line);
    if (worth === null) return t('audit.categoryRuledOut', { declared: points(declared) });

    const effective = round(worth);
    return declared === effective
      ? t('audit.worth', { weight: points(declared) })
      : t('audit.worthReduced', { declared: points(declared), effective: points(effective) });
  };

  /** What one question is worth today; undefined before the first score, null when out. */
  const worthOf = (categoryId: string, questionId: string) => {
    const line = scoreOf(categoryId);
    return line && running ? questionWorth(running, line, questionId) : undefined;
  };

  const ruledOut = (categoryId: string) => {
    const line = scoreOf(categoryId);
    return line ? isRuledOut(line) : false;
  };

  const whole = progress(
    form.categories.flatMap((c) => c.questions).filter((q) => visible.get(q.id) !== false),
    answers,
    { required: detail.requiresSignature, given: detail.signature !== null },
  );

  /**
   * Show only what is still outstanding.
   *
   * <p>Seventy questions with four left in them is a scrolling exercise. Switched off by
   * default: the list is the visit, and hiding most of it is a thing somebody asks for
   * rather than something that happens to them.</p>
   */
  const outstanding = (question: SnapshotQuestion) =>
    !onlyMissing || !isComplete(question, answers.get(question.id));

  return (
    <>
      <PageHeader
        crumbs={[t('nav.audits'), detail.summary.locationCode]}
        title={`${detail.summary.locationCode} · ${detail.summary.locationName}`}
        subtitle={`${detail.summary.formName} v${detail.summary.formVersion}${
          detail.summary.reference ? ` · ${detail.summary.reference}` : ''
        }`}
        aside={
          <>
            {/* Only once there is something to hide, and something left to find. */}
            {!locked && whole.done > 0 && whole.done < whole.total ? (
              <Button
                size="sm"
                variant={onlyMissing ? 'primary' : 'ghost'}
                icon={IconListFilter}
                aria-pressed={onlyMissing}
                onClick={() => setOnlyMissing(!onlyMissing)}
              >
                {t('audit.onlyMissing', { count: whole.total - whole.done })}
              </Button>
            ) : null}
            {/*
              * The figure the auditor is asked for all morning. Labelled "anlık" and not
              * simply shown as a score: half a form answered is not a result, and a number
              * with no word beside it is read as one — then quoted in a meeting, then
              * argued with when the remaining answers move it.
              *
              * Gone once the visit is submitted; from then on the heading above carries the
              * server's own number, which is the one of record.
              */}
            {showScore && !locked ? <RunningScore value={runningFigure} points={byPoints} /> : null}
            <Progress done={whole.done} total={whole.total} />
            {/* What is still on the device. A tick that means nothing is worse than a count. */}
            <SyncChip />
          </>
        }
      />

      <div className="nv-audit">
        {error ? <Alert>{error}</Alert> : null}

        {rejected.length > 0 && (
          // Taken out of the queue because the server will refuse them again tomorrow.
          // Shown rather than retried for ever behind everything else.
          <Alert>
            <strong>{t('audit.rejected', { count: rejected.length })}</strong>
            <ul className="nv-reasons">
              {rejected.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          </Alert>
        )}

        {stuck > 0 && (
          // Not a refusal and not lost: still queued, still owed, and no longer holding
          // everything else up. Said out loud because the screen shows these the moment
          // they are chosen — so without this the auditor sees their evidence sitting
          // there and has no way to know it has not arrived.
          <Alert>
            <strong>{t('audit.stuck', { count: stuck })}</strong>
            <p>{t('audit.stuckHint')}</p>
          </Alert>
        )}

        {locked ? (
          <Alert tone="info">
            {t('audit.submittedOn', {
              when: formatDate(detail.summary.submittedAt ?? '', { dateStyle: 'medium', timeStyle: 'short' }),
              score: detail.summary.score ?? '—',
            })}
          </Alert>
        ) : null}

        {form.categories.map((category) => {
          const seen = counts(category.questions);

          // "Only missing" opens the categories that have something missing. Without this
          // the filter would answer an auditor looking for their four unanswered questions
          // with nine shut headings and no way to tell which of them to open.
          const holdsMissing = onlyMissing && seen.done < seen.total;
          const shut = !expanded.has(category.id) && !holdsMissing;

          return (
            <section key={category.id} className="nv-qcat" data-collapsed={shut || undefined}>
              <button
                type="button"
                className="nv-qcat__head"
                aria-expanded={!shut}
                onClick={() =>
                  setExpanded((was) => {
                    const next = new Set(was);
                    if (shut) next.add(category.id);
                    else next.delete(category.id);
                    return next;
                  })
                }
              >
                <IconChevronDown size={16} />
                <span className="nv-qcat__name">{category.name}</span>

                {/*
                  * What this category is worth today, and what it was worth on the form.
                  * Shown as "40 → 20" rather than as 20 alone, because the designer typed
                  * 40 and anybody who knows that would otherwise be looking at a number
                  * with no explanation. Only when they differ: on most categories, on most
                  * visits, they do not.
                  */}
                {showPoints && scoreOf(category.id) ? (
                  <span className="nv-qcat__worth">
                    {weightLabel(category.id)}
                  </span>
                ) : null}

                {/* The category's own figure, same rules as the one in the heading. */}
                {showScore && !locked ? (
                  <Badge tone="neutral">
                    {formatFigure(categoryFigure(category.id), byPoints)}
                  </Badge>
                ) : null}

                <Progress done={seen.done} total={seen.total} />
              </button>

              {!shut &&
                category.questions
                  .filter((q) => visible.get(q.id) !== false)
                  .filter(outstanding)
                  .map((question) => (
                    <Question
                      key={question.id}
                      question={question}
                      answer={answers.get(question.id)}
                      locked={locked}
                      onAnswer={(body) =>
                        void enqueue({ kind: 'answer', questionId: question.id, body })
                      }
                      onPhoto={(file) => void addEvidence(question.id, file)}
                      refusals={refusedFiles[question.id]}
                      onRemovePhoto={(photoId) =>
                        void enqueue({ kind: 'removePhoto', photoId })
                      }
                      onRaise={() => setRaising({ question, comment: answers.get(question.id)?.comment ?? '' })}
                      onRelink={relink}
                      showPoints={showPoints}
                      worth={worthOf(category.id, question.id)}
                      categoryOut={ruledOut(category.id)}
                    />
                  ))}
            </section>
          );
        })}

        {/* Only when the checklist asked for one, which is read from the snapshot: a
            template that started asking last week does not reach back into this visit. */}
        {detail.requiresSignature && (
          <section className="nv-qcat">
            <div className="nv-qcat__head nv-qcat__head--static">
              <span className="nv-qcat__name">{t('audit.signature')}</span>
              {detail.signature ? null : (
                <Badge tone="na" icon={IconTriangleAlert}>{t('audit.signNeeded')}</Badge>
              )}
            </div>
            <div className="nv-qcard">
              <SignaturePad
                signed={detail.signature}
                busy={locked}
                onSign={(png, name) => void enqueue({ kind: 'sign', blob: png, name })}
                onClear={() => void enqueue({ kind: 'removeSignature' })}
              />
            </div>
          </section>
        )}

        <Field label={t('audit.notes')}>
          {({ id }) => (
            <Textarea
              id={id}
              disabled={locked}
              defaultValue={detail.notes ?? ''}
              onBlur={(e) =>
                e.target.value !== (detail.notes ?? '') &&
                void enqueue({ kind: 'notes', notes: e.target.value })
              }
            />
          )}
        </Field>

        <Button variant="ghost" className="nv-self-start" onClick={() => navigate('/denetimler')}>
          {t('audit.backToList')}
        </Button>

        {!locked && (
          <>
            {running.isComplete ? null : (
              <Alert tone="warning">
                {t('audit.unanswered', { count: running.unansweredQuestionIds.length })}
              </Alert>
            )}

            {/* What the server said last time it was asked. It knows about comments and
                photographs owed, which the local engine does not judge. */}
            {detail.problems.length > 0 ? (
              <Alert tone="warning">
                <strong>{t('audit.blocking', { count: detail.problems.length })}</strong>
                <ul className="nv-reasons">
                  {detail.problems.map((problem) => (
                    <li key={problem.code + JSON.stringify(problem.args)}>
                      {t(problem.code, problem.args)}
                    </li>
                  ))}
                </ul>
              </Alert>
            ) : null}

            {folded.submitting ? <Alert tone="info">{t('audit.submitQueued')}</Alert> : null}

            <Button
              variant="primary"
              size="lg"
              icon={IconSend}
              className="nv-self-start"
              disabled={!running.isComplete || folded.submitting}
              onClick={() => setConfirming(true)}
            >
              {t('audit.submit')}
            </Button>
          </>
        )}
      </div>

      <ConfirmDialog
        open={confirming}
        title={t('audit.submit')}
        tone="primary"
        confirmLabel={t('audit.submitConfirm')}
        onConfirm={() => enqueue({ kind: 'submit' })}
        onClose={() => setConfirming(false)}
      >
        {/* The report goes out automatically, so there is no quiet way back. */}
        <p>{t('audit.submitWarning')}</p>
        {/* Only where the figures are shown at all. A customer who hides them all morning
            does not want the last dialog of the visit to announce one. */}
        {showScore ? <p>{t('audit.submitScore', { score: running.score ?? '—' })}</p> : null}
      </ConfirmDialog>

      {raising ? (
        <RaiseAction
          auditId={detail.summary.id}
          questionId={raising.question.id}
          questionText={raising.question.text}
          comment={raising.comment}
          onClose={() => setRaising(null)}
          onRaised={() => void refresh()}
        />
      ) : null}
    </>
  );
}

/**
 * Two decimals, and no trailing zeros.
 *
 * <p>The weights are decimals on the server and float64 here, so a category worth twenty
 * can arrive as 19.999999999999996. Comparing those two for equality is how "40 → 40"
 * ends up on screen.</p>
 */
const round = (value: number) => Math.round(value * 100) / 100;

/**
 * A worth as the reader writes numbers: 18,75 in Turkish. Since ADR-022 a question or a
 * category can be worth a fraction on the day, and the bare number came out as 18.75.
 */
const points = (value: number) => formatNumber(round(value), { maximumFractionDigits: 2 });

/**
 * What the answers given so far come to.
 *
 * <p>Deliberately quiet — neutral, never coloured by how good the number is. A running
 * figure that turns green at 85 is telling a store manager they have passed before the
 * auditor has reached the stockroom, and the colour is what they remember.</p>
 */
function RunningScore({ value, points }: { value: number | null; points: boolean }) {
  const t = useTranslate();

  return (
    <Badge tone="neutral" title={t('audit.runningHint')}>
      {t('audit.running', { score: formatFigure(value, points) })}
    </Badge>
  );
}

/**
 * A ratio always carries one decimal, as it always has; points carry one only when they
 * have one, so a category answered in full reads 25 and not 25,0.
 */
function formatFigure(value: number | null, points: boolean) {
  if (value === null) return '—';

  return formatNumber(value, { minimumFractionDigits: points ? 0 : 1, maximumFractionDigits: 1 });
}

/** Colour and number together. Colour alone says nothing in sunlight or to some readers. */
function Progress({ done, total }: { done: number; total: number }) {
  const share = total === 0 ? 1 : done / total;
  const tone = share === 1 ? 'comment' : share > 0 ? 'na' : 'neutral';

  return (
    <Badge tone={tone} icon={share === 1 ? IconCheck : undefined}>
      {`${done}/${total}`}
    </Badge>
  );
}

function Question({
  question,
  answer,
  locked,
  onAnswer,
  onPhoto,
  onRemovePhoto,
  onRaise,
  onRelink,
  showPoints,
  worth,
  categoryOut,
  refusals,
}: {
  question: SnapshotQuestion;
  answer: AuditAnswer | undefined;
  locked: boolean;
  showPoints: boolean;

  /**
   * What the question is worth on this visit (ADR-022): more than it says when a neighbour
   * was ruled out. Undefined before anything is scored, null when it is itself out.
   */
  worth?: number | null;

  /** Whether every question in its category is out, so the category itself is. */
  categoryOut?: boolean;
  onAnswer: (body: Parameters<typeof audits.answer>[2]) => void;
  onPhoto: (file: File) => void;
  onRemovePhoto: (photoId: string) => void;
  onRaise: () => void;

  /** A working address for a file whose address did not load, from the server. */
  onRelink: (photoId: string) => Promise<string>;

  /** Files chosen for this question that did not make it, each with the reason. */
  refusals?: string[];
}) {
  const t = useTranslate();
  const [showGuidance, setShowGuidance] = useState(false);
  const [writing, setWriting] = useState(false);
  const camera = useRef<HTMLInputElement>(null);
  const recorder = useRef<HTMLInputElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const videoPicker = useRef<HTMLInputElement>(null);

  const [capturing, setCapturing] = useState<'photo' | 'video' | null>(null);
  const [viewing, setViewing] = useState<number | null>(null);

  // Videos whose tile has already asked for a fresh link. See the video's onError.
  const relinked = useRef(new Set<string>());

  // Settled once, not on every render: it asks the browser about itself and the answer
  // cannot change while the screen is open.
  const [inPage] = useState(canOpenCamera);

  /** Whichever of the three was used, the file goes the same way. */
  const chosen = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onPhoto(file);

    // Cleared, so choosing the same file twice in a row still fires a change.
    e.target.value = '';
  };

  const skipped = isSkipped(answer);

  // What the auditor owes, from the same module the counter uses. Worked out in both
  // places separately is how the header came to read 6/6 on a visit the server refused.
  const stands = completeness(question, answer);
  const { comment: owesComment, photo: owesPhoto } = owed(question, answer);

  return (
    // The marker down the left edge. One glance finds the gaps in a list of seventy,
    // which neither the text nor the counter could do.
    <article className={`nv-qcard is-${stands}`}>
      <header className="nv-qcard__head">
        {question.code ? <span className="nv-qcard__code">{question.code}</span> : null}
        <span className="nv-qcard__text">{question.text}</span>

        {/*
          The state in words as well as in the colour of the stripe. Colour on its own is
          no state at all to somebody who cannot tell these two apart, and it needs a
          legend from everybody else.

          Only the states that ask for something. "Done" says nothing on purpose: most of
          the list is done, and a badge on every finished question is a wall of green with
          the two that matter hidden in it — the absence of a demand is the message.
        */}
        {stands === 'done' ? (
          <Badge tone="comment" icon={IconCheck}>{t('audit.answered')}</Badge>
        ) : null}

        {stands === 'empty' ? (
          <Badge tone="na" icon={IconCircleAlert}>{t('audit.notAnswered')}</Badge>
        ) : null}

        {stands === 'owing' ? (
          <Badge tone="na" icon={IconTriangleAlert}>
            {owesComment && owesPhoto
              ? t('audit.owesBoth')
              : owesComment
                ? t('audit.owesComment')
                : t('audit.owesPhoto')}
          </Badge>
        ) : null}
        {question.critical !== 'None' ? (
          <Badge tone="critical" icon={IconTriangleAlert}>{t('questions.critical')}</Badge>
        ) : null}

        {/*
          * What the question is worth, beside the question. Asked for, and the reason is
          * the points model: a number the designer typed to mean "this one matters most"
          * is of no use to the auditor if only the designer ever sees it.
          *
          * Hidden where the customer wants it hidden — some do, so that a store cannot
          * work out which four questions to care about the night before a visit.
          */}
        {showPoints && question.weight > 0 && !skipped ? (
          <Badge tone="neutral">{t('audit.worth', { weight: points(worth ?? question.weight) })}</Badge>
        ) : null}
      </header>

      {!skipped && (
        <Control question={question} answer={answer} locked={locked} onAnswer={onAnswer} />
      )}

      {skipped ? (
        <span className="nv-row">
          {/*
            * Outlined red, against the filled red a failed critical check carries. Both
            * can appear on one screen and both are the auditor's doing; what tells them
            * apart is weight, not hue.
            */}
          <Badge tone="excluded" icon={IconCircleSlash}>
            {t(answer?.structurallyNotApplicable ? 'audit.exempt' : 'audit.skipped')}
          </Badge>

          {/*
            * Where its points went — the arithmetic the store will ask about. To the rest of
            * the category (ADR-022), or with the whole category to the others. A form
            * published before the rule still says its points left the form, because for
            * audits against it they did.
            */}
          {showPoints && question.weight > 0 ? (
            <span className="nv-subtle">
              {question.notApplicable.scope === 'Form'
                ? t('audit.pointsLeft', { points: question.weight })
                : categoryOut
                  ? t('audit.pointsSpreadForm')
                  : t('audit.pointsSpread', { points: question.weight })}
            </span>
          ) : null}
        </span>
      ) : null}

      <div className="nv-row">
        {/* A finding somebody has to do something about. Raised here because here is
            where it is noticed, and the auditor is standing in the stockroom. */}
        {!locked && !skipped ? (
          <Button variant="ghost" size="sm" icon={IconClipboardList} onClick={() => onRaise()}>
            {t('raise.open')}
          </Button>
        ) : null}

        {/*
          * Green once something has been written, so a list of seventy says at a glance
          * which ones carry a note — which is what the open box used to say by being
          * full, and what nothing said once it was put away.
          *
          * Primary when one is owed and missing: the demand and the thing that answers it
          * are the same control, as with the photograph beside it.
          */}
        {!skipped ? (
          <Button
            variant={
              owesComment && !answer?.comment ? 'primary' : answer?.comment ? 'success' : 'ghost'
            }
            size="sm"
            icon={IconMessageSquare}
            onClick={() => setWriting(true)}
          >
            {t(
              owesComment && !answer?.comment
                ? 'audit.commentNeededShort'
                : answer?.comment
                  ? 'audit.commentWritten'
                  : 'audit.comment',
            )}
          </Button>
        ) : null}

        {/* What the question was written to mean. Only where there is any. */}
        {question.guidance ? (
          <Button
            variant="ghost"
            size="sm"
            icon={IconInfo}
            aria-expanded={showGuidance}
            onClick={() => setShowGuidance(!showGuidance)}
          >
            {t('audit.guidance')}
          </Button>
        ) : null}

        {question.notApplicable.allowed && !answer?.structurallyNotApplicable && !locked ? (
          <Button
            variant="ghost"
            size="sm"
            icon={IconCircleSlash}
            onClick={() => onAnswer(skipped ? { clear: true } : { notApplicable: true })}
          >
            {t(skipped ? 'audit.unskip' : 'audit.skip')}
          </Button>
        ) : null}

        {!locked && !skipped && (
          <>
            {/*
              * Three inputs rather than one, because `capture` is not a setting a single
              * input can be talked out of: with it, a phone opens the camera and offers no
              * way to the gallery; without it, it opens the gallery and offers the camera
              * only as one choice among the pickers. The auditor asked for both, so there
              * is one input per intention and a menu to say which.
              *
              * The third accepts either kind: a file already on the device is a file, and
              * making somebody say in advance whether it moves is a question with no
              * purpose.
              */}
            <input
              ref={camera}
              type="file"
              accept={PHOTOS}
              capture="environment"
              className="nv-sr-only"
              aria-label={t('audit.takePhoto')}
              onChange={chosen}
            />
            <input
              ref={recorder}
              type="file"
              accept={VIDEOS}
              capture="environment"
              className="nv-sr-only"
              aria-label={t('audit.takeVideo')}
              onChange={chosen}
            />
            <input
              ref={picker}
              type="file"
              accept={galleryAccept(`${PHOTOS},${VIDEOS}`, navigator.userAgent)}
              className="nv-sr-only"
              aria-label={t('audit.fromDevice')}
              onChange={chosen}
            />
            {/* A gallery picker that takes video only — the explicit "upload a video" the auditor
                asked for, next to "record a video", without the capture hint that would open the
                camera instead of the gallery. */}
            <input
              ref={videoPicker}
              type="file"
              accept={galleryAccept(VIDEOS, navigator.userAgent)}
              className="nv-sr-only"
              aria-label={t('audit.videoFromGallery')}
              onChange={chosen}
            />

            {/* The demand and the thing that answers it are the same control. A quiet
                ghost button beside a badge saying "required" asks somebody to join two
                parts of a sentence that are a centimetre apart. */}
            <RowMenu
              label={t('audit.addEvidence')}
              items={[
                {
                  label: t('audit.takePhoto'),
                  icon: IconCamera,
                  // The page's own camera where it is allowed to run, the phone's where it
                  // is not. See canOpenCamera: the second path is not a fallback for old
                  // browsers, it is the only one on an installation served over http.
                  onSelect: () => (inPage ? setCapturing('photo') : camera.current?.click()),
                },
                {
                  label: t('audit.takeVideo'),
                  icon: IconVideo,
                  onSelect: () => (inPage ? setCapturing('video') : recorder.current?.click()),
                },
                { label: t('audit.videoFromGallery'), icon: IconVideo, onSelect: () => videoPicker.current?.click() },
                { label: t('audit.fromDevice'), icon: IconUpload, onSelect: () => picker.current?.click() },
              ]}
              trigger={(open, toggle, menuId) => (
                <Button
                  variant={owesPhoto && (answer?.photos.length ?? 0) === 0 ? 'primary' : 'ghost'}
                  size="sm"
                  icon={IconCamera}
                  aria-haspopup="menu"
                  aria-expanded={open}
                  aria-controls={open ? menuId : undefined}
                  onClick={toggle}
                >
                  {t(
                    owesPhoto && (answer?.photos.length ?? 0) === 0
                      ? 'audit.photoNeeded'
                      : 'audit.addEvidence',
                  )}
                </Button>
              )}
            />
          </>
        )}

        {(answer?.photos.length ?? 0) > 0 ? (
          <Badge tone="photo" icon={IconCamera}>
            {t('audit.photoCount', { count: answer!.photos.length })}
          </Badge>
        ) : null}

      </div>

      {/* Where the file was added, which is where the auditor is looking. */}
      {refusals && refusals.length > 0 ? (
        <Alert>
          <strong>{t('audit.fileNotAdded')}</strong>
          <ul className="nv-reasons">
            {refusals.map((reason, index) => (
              <li key={`${index}:${reason}`}>{reason}</li>
            ))}
          </ul>
        </Alert>
      ) : null}

      {(answer?.photos.length ?? 0) > 0 && (
        <div className="nv-photos">
          {answer!.photos.map((photo, index) => (
            <figure key={photo.id}>
              {/*
                * A video is not an image and an <img> pointed at one draws nothing — a
                * broken-picture icon where the evidence should be. Given its own element,
                * with controls, because the auditor's reason for looking at it is to play
                * it and a custom player would be a player to maintain.
                *
                * preload="metadata" so the list does not pull a hundred megabytes down the
                * moment the category is opened: the first frame and the duration are what
                * a thumbnail needs.
                */}
              {photo.contentType?.startsWith('video/') ? (
                // No controls on the tile. They would take the tap meant for opening it,
                // and a play button six millimetres wide is not what anybody is aiming at
                // — the whole tile is the target, and the video plays where it can be
                // seen.
                <button
                  type="button"
                  className="nv-photos__play"
                  onClick={() => setViewing(index)}
                  aria-label={t('audit.videoAlt', { code: question.code ?? question.text, n: index + 1 })}
                >
                  <video
                    className="nv-photos__video"
                    src={photo.url ?? undefined}
                    preload="metadata"
                    muted
                    playsInline
                    // Once, as the thumbnail does: a link that still fails after a fresh
                    // one is a file that is not there, and asking again would not end.
                    onError={() => {
                      if (relinked.current.has(photo.id)) return;
                      relinked.current.add(photo.id);
                      void onRelink(photo.id).catch(() => {});
                    }}
                  />
                  <span className="nv-photos__play-mark" aria-hidden="true">
                    <IconVideo size={20} />
                  </span>
                </button>
              ) : (
                <Thumbnail
                  url={photo.url}
                  size={84}
                  // Not deferred: there are a handful per question, and the one that matters
                  // most is the photograph the auditor took a second ago.
                  lazy={false}
                  refreshUrl={() => onRelink(photo.id)}
                  onOpen={() => setViewing(index)}
                  alt={t('audit.photoAlt', { code: question.code ?? question.text, n: index + 1 })}
                />
              )}
              {!locked ? (
                <IconButton
                  label={t('common.delete')}
                  icon={IconTrash2}
                  tone="danger"
                  onClick={() => onRemovePhoto(photo.id)}
                />
              ) : null}
            </figure>
          ))}
        </div>
      )}

      {/*
        * The guidance the question was written with, and the comment the auditor writes
        * back. Both behind buttons rather than open on the card.
        *
        * <p>The comment used to sit open under every question. On a ninety-question form
        * that is ninety empty boxes to scroll past to reach the four that are owed, and
        * the one that matters is indistinguishable from the eighty-nine that are not.
        * Asked for directly: put it behind a button, like raising an action.</p>
        */}
      {showGuidance && question.guidance ? (
        <p className="nv-qcard__guidance">{question.guidance}</p>
      ) : null}

      {viewing !== null && answer ? (
        <MediaViewer
          items={answer.photos}
          startAt={viewing}
          label={question.code || question.text}
          onRefresh={(photoId) => void onRelink(photoId).catch(() => {})}
          onClose={() => setViewing(null)}
        />
      ) : null}

      {capturing ? (
        <CameraCapture
          mode={capturing}
          code={question.code || question.text.slice(0, 20)}
          onCaptured={onPhoto}
          onClose={() => setCapturing(null)}
        />
      ) : null}

      <Drawer
        open={writing}
        title={t('audit.comment')}
        onClose={() => setWriting(false)}
        footer={
          <Button onClick={() => setWriting(false)}>{t('common.close')}</Button>
        }
      >
        <p className="nv-hint">{question.code ? `${question.code} · ` : ''}{question.text}</p>

        <Field
          label={owesComment ? `${t('audit.comment')} — ${t('audit.commentNeeded')}` : t('audit.comment')}
        >
          {({ id }) => (
            <Textarea
              id={id}
              disabled={locked}
              rows={8}
              className={owesComment && !answer?.comment ? 'is-owed' : undefined}
              defaultValue={answer?.comment ?? ''}
              onBlur={(e) =>
                e.target.value !== (answer?.comment ?? '') && onAnswer({ comment: e.target.value })
              }
            />
          )}
        </Field>
      </Drawer>
    </article>
  );
}

/** What this answer has earned, 0 to 1. Mirrors FormQuestion.EarnedRatio. */

function Control({
  question,
  answer,
  locked,
  onAnswer,
}: {
  question: SnapshotQuestion;
  answer: AuditAnswer | undefined;
  locked: boolean;
  onAnswer: (body: Parameters<typeof audits.answer>[2]) => void;
}) {
  const t = useTranslate();

  switch (question.type) {
    case 'YesNo':
      return (
        <div className="nv-choices">
          {[true, false].map((value) => (
            <button
              key={String(value)}
              type="button"
              disabled={locked}
              aria-pressed={answer?.yesNo === value}
              className={value ? 'nv-choice' : 'nv-choice nv-choice--no'}
              onClick={() => onAnswer({ yesNo: value })}
            >
              {t(value ? 'audit.yes' : 'audit.no')}
            </button>
          ))}
        </div>
      );

    case 'Scale': {
      const min = question.scale?.min ?? 0;
      const max = question.scale?.max ?? 5;

      // The device offers 1…max, as the process document specifies, while the stored scale
      // starts at zero — which is what makes the earned share come out as value / max.
      const steps = Array.from({ length: max - Math.max(min, 1) + 1 }, (_, i) => Math.max(min, 1) + i);

      return (
        <div className="nv-choices">
          {steps.map((value) => (
            <button
              key={value}
              type="button"
              disabled={locked}
              aria-pressed={answer?.scaleValue === value}
              className="nv-choice"
              onClick={() => onAnswer({ scaleValue: value })}
            >
              {value}
            </button>
          ))}
        </div>
      );
    }

    case 'SingleChoice':
      return (
        <div className="nv-choices nv-choices--stack">
          {(question.options ?? []).map((option) => (
            <button
              key={option.id}
              type="button"
              disabled={locked}
              aria-pressed={answer?.selectedOptionIds.includes(option.id) ?? false}
              className="nv-choice"
              onClick={() => onAnswer({ selectedOptionIds: [option.id] })}
            >
              {option.text}
            </button>
          ))}
        </div>
      );

    case 'MultipleChoice': {
      const chosen = answer?.selectedOptionIds ?? [];

      return (
        <div className="nv-choices nv-choices--stack">
          {(question.options ?? []).map((option) => {
            const on = chosen.includes(option.id);

            return (
              <button
                key={option.id}
                type="button"
                disabled={locked}
                aria-pressed={on}
                className="nv-choice nv-choice--tick"
                onClick={() =>
                  onAnswer({
                    selectedOptionIds: on
                      ? chosen.filter((x) => x !== option.id)
                      : [...chosen, option.id],
                  })
                }
              >
                {on ? <IconCheck size={16} /> : <IconSquare size={16} />}
                {option.text}
              </button>
            );
          })}
        </div>
      );
    }

    case 'Text':
      return (
        <Textarea
          disabled={locked}
          aria-label={question.text}
          placeholder={t('audit.textPlaceholder')}
          defaultValue={answer?.text ?? ''}
          onBlur={(e) => e.target.value !== (answer?.text ?? '') && onAnswer({ text: e.target.value })}
        />
      );

    default:
      // A server newer than this build. Saying so beats a blank space where a control belongs.
      return <Badge tone="critical" icon={IconTriangleAlert}>{t('audit.unknownType')}</Badge>;
  }
}
